import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Space } from 'antd';
import { FileTextOutlined, ToolOutlined } from '@ant-design/icons';
import useBreadcrumb from 'hooks/useBreadcrumb';
import { StyledPageWrapper } from 'components/new-ui/base/wrappers/styles';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import Button from 'components/new-ui/base/button/Button';
import Segmented from 'components/new-ui/base/segmented/Segmented';
import useSession from 'hooks/useSession';
import { hasPermissions } from 'config/roles/hasPermissions';
import useInvoices from 'hooks/useInvoices';
import { ListInvoicesSortParams } from 'store/invoices/types';
import { getTableChangeParams, SortDirection, SortDirections } from 'utils/tableHelpers';
import { getInvoicesDashboardData } from 'store/invoices/client';
import useInvoicesFilters from '../invoice-details/useInvoicesFilters';
import DashboardHeader from './components/DashboardHeader';
import InvoicesTable from './components/InvoicesTable';
import DatePicker from 'components/new-ui/base/date-picker/DatePicker';
import PopoverButtonsGroup from 'components/new-ui/composed/popover-buttons-group/PopoverButtonsGroup';
import { StyledPopoverButton } from 'components/new-ui/composed/popover-buttons-group/styles';
import { StyledTag } from 'components/new-ui/base/tag/styles';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import CustomerAutocomplete from 'components/new-ui/composed/selects/CustomerAutocomplete';
import useCustomers from 'hooks/useCustomers';
import useDebounce from 'utils/useDebounce';
import { getCustomer } from 'store/customers/client';
import useDuplicateInvoice from '../hooks/useDuplicateInvoice';
import { DashboardInvoicesData, initialInvoicesDashboardData } from '../types';
import { IndividualType } from 'store/individuals/individual';
import useInvoicesConfig from 'hooks/useInvoicesConfig';
import { mapInvoiceStatusCountsToDashboardData } from '../utils';
import { useExportInvoice } from '../hooks/useExportInvoice';
import { useArchiveInvoice } from '../hooks/useArchiveInvoice';

const InvoicesPage = () => {
    const { userInfo } = useSession();
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const { customers, listCustomers } = useCustomers();
    const { invoicesListResponse, listInvoices, isLoading } = useInvoices();
    const navigate = useNavigate();
    const {
        queryInputVal,
        tags,
        appliedParams,
        setStatuses,
        setArchived,
        setPage,
        setSize,
        clearFilter,
        clearAll,
        setDate,
        setQueryInputVal,
        setSortState,
        setCustomerId,
    } = useInvoicesFilters();
    const { duplicateInvoice } = useDuplicateInvoice();
    const { exportInvoice } = useExportInvoice();
    const { getConfig } = useInvoicesConfig();
    const { archiveInvoice, unarchiveInvoice } = useArchiveInvoice(async () => {
        await listInvoices(appliedParams);
        await fetchDashboardCounts();
    });

    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [invoicesCounts, setInvoicesCounts] = useState<DashboardInvoicesData>(initialInvoicesDashboardData);
    const [isCountsLoading, setIsCountsLoading] = useState(true);
    const [showBulkExportPopover, setShowBulkExportPopover] = useState(false);
    const [customerInputValue, setCustomerInputValue] = useState<string | undefined>(undefined);
    const [customerLabelById, setCustomerLabelById] = useState<Record<string, string>>({});
    const [hasUserEditedCustomerInput, setHasUserEditedCustomerInput] = useState(false);

    const { archived, page, size, sort, sortDirection, listInvoiceStatus, exactDate } = appliedParams;

    const canUpdateInvoices = hasPermissions(userInfo, 'invoices', 'UPDATE');
    const canUpdateConfig =
        userInfo?.role !== undefined &&
        [IndividualType.FREIGHT_FORWARDER_ADMIN, IndividualType.ACCOUNTS_MANAGER, IndividualType.SUPER_ADMIN].includes(
            userInfo.role
        );

    const upsertCustomerLabel = (id: string, label: string) => {
        setCustomerLabelById((prev) => ({ ...prev, [id]: label }));
    };

    const querySearchCustomers = useCallback(
        async (query: string) => {
            if (query) {
                const params = { query };
                await listCustomers(params);
            } else {
                await listCustomers({ size: 200 });
            }
        },
        [listCustomers]
    );

    const debouncedSearchCustomers = useDebounce(querySearchCustomers, 500);

    const handleSelectCustomer = async (id: string) => {
        const c = customers.list.find((x) => x.id === id);
        const label = c?.name ?? c?.email ?? c?.contact?.name ?? id;

        setHasUserEditedCustomerInput(false);
        setCustomerInputValue(label);
        setCustomerId(id);
        upsertCustomerLabel(id, label);
        setPage(0);
    };

    const handleSearchCustomers = (val: string) => {
        // User is actively editing the input
        setHasUserEditedCustomerInput(true);
        setCustomerInputValue(val);

        if (!val) {
            // Input cleared via backspace – clear filter
            setCustomerId(undefined);
            setPage(0);
        }

        debouncedSearchCustomers(val);
    };

    const handleExportCsv = (invoiceId: string, orderNumber: string) =>
        exportInvoice({ type: 'csv', invoiceId, orderNumber });

    const handleDownloadPdf = async (invoiceId: string, orderNumber: string) => {
        exportInvoice({ type: 'pdf', invoiceId, orderNumber });
    };

    const handleDuplicateInvoice = async (invoiceId: string) => {
        duplicateInvoice(invoiceId);
    };

    const handleBulkExportCsv = () => exportInvoice({ type: 'bulk-csv', invoiceIds: selectedRowKeys });
    const handleBulkExportPdfAsZip = () => exportInvoice({ type: 'bulk-pdf-zip', invoiceIds: selectedRowKeys });

    const fetchDashboardCounts = useCallback(async () => {
        setIsCountsLoading(true);
        try {
            const data = await getInvoicesDashboardData({ archived: !!archived });
            setInvoicesCounts(mapInvoiceStatusCountsToDashboardData(data.list));
        } finally {
            setIsCountsLoading(false);
        }
    }, [archived]);

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys.map((k) => k.toString()));
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const hasSelected = selectedRowKeys.length > 0;

    useEffect(() => {
        setBreadcrumbRoutes([
            { breadcrumbName: 'Home', path: '/home' },
            { breadcrumbName: 'Invoices', path: '' },
        ]);
    }, [setBreadcrumbRoutes]);

    useEffect(() => {
        fetchDashboardCounts();
    }, [fetchDashboardCounts]);

    // Get initial invoices list
    useEffect(() => {
        listInvoices(appliedParams);
    }, [appliedParams, listInvoices]);

    // When customerId is cleared from filters, reset input + edit flag
    useEffect(() => {
        if (!appliedParams.customerId) {
            setHasUserEditedCustomerInput(false);
            setCustomerInputValue(undefined);
        }
    }, [appliedParams.customerId]);

    // Keep input in sync with selected customer label, but do NOT override user edits
    useEffect(() => {
        const id = appliedParams.customerId;

        if (!id) return;

        if (!customerLabelById[id]) {
            getCustomer(id).then((c) => {
                const label = c.name ?? c.email ?? id;
                upsertCustomerLabel(id, label);

                // Only apply if user is not editing manually
                setCustomerInputValue((current) => (hasUserEditedCustomerInput ? current : label));
            });

            return;
        }

        if (!hasUserEditedCustomerInput) {
            setCustomerInputValue(customerLabelById[id]);
        }
    }, [appliedParams.customerId, customerLabelById, hasUserEditedCustomerInput]);

    // Preload the config
    useEffect(() => {
        getConfig();
    }, [getConfig]);

    return (
        <StyledPageWrapper>
            <Space style={{ width: '100%' }} direction="vertical" size={24}>
                <PageHeader
                    title="Invoices"
                    icon={<FileTextOutlined />}
                    sidebar={
                        <Space size={16}>
                            <Segmented
                                style={{ marginRight: 16 }}
                                onChange={(val) => setArchived(val === 'active' ? false : true)}
                                options={[
                                    {
                                        value: 'active',
                                        label: 'Active',
                                    },
                                    {
                                        value: 'archived',
                                        label: 'Archived',
                                    },
                                ]}
                            />
                            {canUpdateConfig && (
                                <Button onClick={() => navigate('config')} icon={<ToolOutlined />} type="primary">
                                    Configure
                                </Button>
                            )}
                        </Space>
                    }
                />

                <DashboardHeader
                    archived={archived}
                    activeFilter={listInvoiceStatus[0]}
                    isCountsLoading={isCountsLoading}
                    onFilterChange={(status) => {
                        if (status) {
                            setStatuses([status]);
                            setPage(0);
                        } else {
                            setStatuses([]);
                            setPage(0);
                        }
                    }}
                    invoicesCounts={invoicesCounts}
                />

                <Row justify="space-between">
                    <Space>
                        <SearchBar
                            inputPlaceholder="Search invoices"
                            value={queryInputVal}
                            onSearch={(val) => {
                                setQueryInputVal(val);
                                setPage(0);
                            }}
                            onClear={() => {
                                setQueryInputVal('');
                                setPage(0);
                            }}
                        />
                        <CustomerAutocomplete
                            placeholder="Filer by customer"
                            value={customerInputValue}
                            inputStyle={{ width: '100%' }}
                            customers={customers.list}
                            handleSelectCustomer={handleSelectCustomer}
                            search={handleSearchCustomers}
                            setCustomer={setCustomerId}
                            disabled={false}
                            dropdownMatchSelectWidth={false}
                            onClear={() => {
                                setHasUserEditedCustomerInput(false);
                                setCustomerInputValue(undefined);
                                setCustomerId(undefined);
                                setPage(0);
                            }}
                        />
                    </Space>
                    <Space>
                        <DatePicker
                            value={exactDate ? moment(exactDate, 'YYYY-MM-DD') : undefined}
                            onChange={(val) => {
                                if (!val) {
                                    setDate(undefined);
                                    return;
                                }
                                setDate(val.format('YYYY-MM-DD'));
                            }}
                        />
                        <PopoverButtonsGroup
                            buttonEl={<Button disabled={!hasSelected}>Export</Button>}
                            open={showBulkExportPopover}
                            onOpenChange={(next) => setShowBulkExportPopover(next)}
                        >
                            <StyledPopoverButton
                                onClick={() => {
                                    handleBulkExportCsv();
                                    setShowBulkExportPopover(false);
                                }}
                            >
                                Export CSV
                            </StyledPopoverButton>
                            <StyledPopoverButton
                                onClick={() => {
                                    handleBulkExportPdfAsZip();
                                    setShowBulkExportPopover(false);
                                }}
                            >
                                Export PDF
                            </StyledPopoverButton>
                        </PopoverButtonsGroup>
                    </Space>
                </Row>

                <Row>
                    {tags.map((t) => {
                        return (
                            <StyledTag
                                key={`${t.key}-${t.value}`}
                                closable
                                onClose={(e) => {
                                    e.preventDefault();
                                    clearFilter(t.key);
                                }}
                            >
                                {t.value}
                            </StyledTag>
                        );
                    })}

                    {tags?.length > 0 && (
                        <Button type="link" onClick={clearAll}>
                            Clear All Filters
                        </Button>
                    )}
                </Row>

                <InvoicesTable
                    loading={isLoading}
                    data={invoicesListResponse.list}
                    showActions={canUpdateInvoices}
                    actions={{
                        archive: (invoiceId) => archiveInvoice(invoiceId),
                        unarchive: (invoiceId) => unarchiveInvoice(invoiceId),
                        downloadPdf: (invoiceId, orderNumber) => handleDownloadPdf(invoiceId, orderNumber),
                        duplicate: (invoiceId) => handleDuplicateInvoice(invoiceId),
                        exportCsv: (invoiceId, orderNumber) => handleExportCsv(invoiceId, orderNumber),
                    }}
                    onChange={(antPagination, _filters, sorter) => {
                        const raw = getTableChangeParams({ pagination: antPagination, sorter });

                        const { page, size, sortParameter, sortDirection } = (raw || {}) as {
                            page?: number;
                            size?: number;
                            sortParameter?: string;
                            sortDirection?: SortDirection;
                        };

                        setPage((p) => (typeof page === 'number' ? page : p));
                        setSize((p) => (typeof size === 'number' ? size : p));

                        if (!sortParameter || !sortDirection) {
                            setSortState({ sort: undefined, sortDirection: undefined });
                        } else {
                            setSortState({
                                sort: sortParameter as ListInvoicesSortParams,
                                sortDirection: sortDirection as SortDirections,
                            });
                        }
                    }}
                    pagination={{
                        current: page + 1,
                        pageSize: size,
                        total: invoicesListResponse.total,
                        position: ['bottomCenter'],
                        showSizeChanger: true,
                        hideOnSinglePage: true,
                    }}
                    onRow={(record) => ({
                        onClick: (e) => {
                            const target = e.target as HTMLElement;
                            if (target.closest('.no-row-nav')) {
                                return;
                            }
                            navigate(`/invoices/${record.id}`);
                        },
                        style: { cursor: 'pointer' },
                    })}
                    rowSelection={canUpdateInvoices ? rowSelection : undefined}
                    currentSortDirection={sortDirection}
                    currentSortParam={sort}
                />
            </Space>
        </StyledPageWrapper>
    );
};

export default InvoicesPage;
